import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import mixpanel from 'mixpanel-browser';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Button, Link, TextField } from '@mui/material';
import NavBar from '../NavBar.js';
import { Footer } from '../Footer/Footer.js';
import SignInImg from './Finance-Loan-Trade-Mortgage.png';
import { forgetPassword } from '../../actions/index.js';
import { getCsrfToken } from '../../actions';
import { useLocalStorage } from '../../CustomHooks/useLocalStorage';
import { Loader } from '../Elements/Loader.js';
import { getOrSetAnonymousUserId } from '../../utils/index';
import { TOKEN, handleSettingsClick, LOGOUT } from '../../constants';
import {
  email,
  emailLabel,
  emailRequired,
  emailPattern,
  enterEmail,
  FORGOT_PASSWORD_HEADER,
  FORGOT_PASSWORD_PARAGRAPH_TEXT,
  LOG_IN,
  PASSWORD_RESET_LINK_SENT,
  REMEMBER_PASSWORD,
  SEND,
} from '../../constants/Forms';

import './ForgetPassword.css';

export const ForgetPassword = () => {
  const [userId, setUserId] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm();
  const navigate = useNavigate();
  const { loading, jwt, csrfToken } = useSelector(
    ({ userReducer: { loading, jwt, csrfToken } }) => ({
      loading,
      jwt,
      csrfToken,
    }),
  );

  const [value] = useLocalStorage(TOKEN);
  const anonymousId = getOrSetAnonymousUserId();

  useEffect(() => {
    if (!csrfToken) {
      dispatch(getCsrfToken());
    }
  }, [csrfToken]);

  useEffect(() => {
    // Redirect to the profile page
    if (value || jwt) {
      if (!localStorage.getItem(TOKEN)) {
        return;
      }

      const user = atob(localStorage.getItem(TOKEN)?.split('.')?.[1]);

      const parsedUser = JSON.parse(user);

      if (parsedUser) {
        const date = new Date(parsedUser.exp * 1000);
        const now = new Date();
        if (date < now) {
          handleSettingsClick[LOGOUT]?.(dispatch, parsedUser);
          return;
        }
        navigate('/dashboard');
      } else {
        setUserId(anonymousId);
      }
    } else {
      setUserId(anonymousId);
    }
  }, [value, jwt]);

  const onSubmit = async (data) => {
    try {
      await dispatch(forgetPassword({ email: data.email, csrfToken })).unwrap();
      setEmailSubmitted(true);
    } catch (error) {
      setEmailSubmitted(false);
      console.error('Forget password error:', error);
    }
    navigate('/verify_password', { state: { email: data.email } });
  };

  const handleOnUpKeyMixPanelTracking = (formField) => {
    mixpanel.track('Forget Password Form', {
      [formField]: getValues(formField) || formField,
      path: '/forgot_password',
      distinct_id: userId,
    });
  };

  return (
    <>
      <NavBar currentPath="" />
      <div className="forget-password-wrapper">
        <header className="forget-password-header-wrapper">
          <div className="sign-up-log-in-image-wrapper">
            <img src={SignInImg} className="sign-up-log-in-image" />
          </div>
          <form className="forget-password-form" onSubmit={handleSubmit(onSubmit)}>
            <h5 className="forget-password-header">{FORGOT_PASSWORD_HEADER}</h5>
            <p className="forget-password-header-detail">
              {emailSubmitted ? PASSWORD_RESET_LINK_SENT : FORGOT_PASSWORD_PARAGRAPH_TEXT}
            </p>

            <div className="forget-password-input-wrapper">
              <label htmlFor={email} className="email">
                {enterEmail}
              </label>
              <TextField
                className={email}
                error={!!errors[email]}
                helperText={errors[email]?.message}
                id={email}
                name={email}
                placeholder={emailLabel}
                variant="outlined"
                autoComplete="off"
                sx={{ backgroundColor: 'rgb(240, 240, 240)', borderRadius: '10px', mt: 1 }}
                {...register(email, {
                  required: emailRequired,
                  pattern: emailPattern,
                })}
                disabled={emailSubmitted}
                onKeyUp={() => handleOnUpKeyMixPanelTracking(email)}
              />
            </div>
            <div>
              <Link variant="outline" id="loginLink" href="/login">
                {REMEMBER_PASSWORD}
                <strong>{LOG_IN}</strong>
              </Link>
            </div>
            <div>
              <Button className="submitBtn" type="submit" disabled={loading}>
                {loading ? <Loader /> : SEND}
              </Button>
            </div>
          </form>
        </header>
      </div>
      <Footer currentPath={'/forget_password'} />
    </>
  );
};
